/* eslint-disable import/no-cycle */
import { createSelectors } from '@leverege/ui-redux';
import Path from '@leverege/path';
import equals from 'fast-deep-equal';
import Constants from '../Constants.js';
import Interface from './Interface.js';
import Access from './Access.js';
const defaultState = {
  data: {
    /*
      <path> : {
        <queryName> : {
          status: ...
          items : [ id0, id1, id2..]
          id : id
        }
      }
    */
  },
  lookup: {
    /*
      <id> : { ...data... }
    */
  },
  reverseLookup: {
    /*
      <id> : {
        <path> : {
          <queryName> : {
            ....additionalInfo
          }
        }
      }
    */
  }
};
const LIST = 'list';
const TABLE = 'table';
const ITEM = 'item';

// eslint-disable-next-line default-param-last
function reducer(state = defaultState, action) {
  switch (action.type) {
    case Constants.INTERFACE_BULK:
      return bulk(state, action);
    case Constants.INTERFACE_STATUS:
      return status(state, action.path, action);
    case Constants.INTERFACE_LIST:
      return list(state, null, action);
    case Constants.INTERFACE_GET:
      return get(state, null, action);
    case Constants.INTERFACE_CREATE_SET:
    case Constants.INTERFACE_CREATE_ADD:
      return create(state, null, action);
    case Constants.INTERFACE_UPDATE:
      return update(state, null, action);
    case Constants.INTERFACE_REMOVE:
      return remove(state, null, action);
    case Constants.INTERFACE_GET_HISTORY:
      return history(state, action.queryName, action);
    case Constants.INTERFACE_LIST_USER:
      return list(state, getPrefix(action.parentId, 'user'), action);
    case Constants.INTERFACE_GET_USER:
      return get(state, getPrefix(action.parentId, 'user'), action);
    case Constants.INTERFACE_CREATE_USER:
      return create(state, getPrefix(action.parentId, 'user'), action);
    case Constants.INTERFACE_UPDATE_USER:
      return update(state, getPrefix(action.parentId, 'user'), action);
    case Constants.INTERFACE_REMOVE_USER:
      return remove(state, getPrefix(action.parentId, 'user'), action);
    case Constants.INTERFACE_SEND_OUTBOUND_DEVICE_MESSAGE:
      return sendMessage(state, `${action.parentId}.message/`, action);
    case Constants.INTERFACE_FILTER:
      return filter(state, action);
    default:
      return state;
  }
}
function getQueryName(qName) {
  return qName || Constants.INTERFACE_DEFAULT_QUERY_NAME;
}

/**
 * Shallow copies the state, as well as the lookup and data if needed
 **/
function copy(state, copyData = true, copyLookup = true, copyReverseLookup = true) {
  const nState = {
    ...state
  };
  if (copyLookup) {
    nState.lookup = {
      ...state.lookup
    };
  }
  if (copyData) {
    nState.data = {
      ...state.data
    };
  }
  if (copyReverseLookup) {
    nState.reverseLookup = {
      ...state.reverseLookup
    };
  }
  return nState;
}
function getPrefix(parentId, subtype) {
  return parentId && subtype ? `${parentId}.${subtype}.` : null;
}
function isTableResult(action) {
  return action.path && action.path.indexOf('*') >= 0;
}
function isSingleResult(state, action) {
  const {
    path,
    queryName
  } = action;
  const qName = getQueryName(queryName);
  return !!state.data?.[path]?.[qName]?.id;
}

// this is a lot faster than redux.bulk, but only handles a list of interface actions
function bulk(state, action) {
  if (!Array.isArray(action?.actions)) {
    return state;
  }
  const {
    actions
  } = action;
  let nState = state;
  for (let i = 0; i < actions.length; i++) {
    nState = reducer(nState, actions[i]);
  }
  return nState;
}

/*
  get a list of all the places a device is referenced. This structure is optimized
  for read over write. Its fast to check if a device is referenced in a given place, but
  more expensive to get a list of all the places a device is referenced
 */
function getRefs(id, revLookup) {
  const obj = revLookup[id];
  // id is nowhere
  if (!obj) {
    return [];
  }
  const paths = Object.entries(obj);
  const refs = [];
  paths.forEach(([path, queries]) => {
    // id might exist at this path, but isn't reff'ed in any current searches
    if (!queries) {
      return;
    }
    Object.entries(queries).forEach(([queryName, value]) => {
      refs.push({
        path,
        queryName,
        value
      });
    });
  });
  return refs;
}

/*
  recalcs a given objects referenced objects. Ref's can be of three types,
  LIST, OBJECT, or TABLE.
 */
function recalc(state, action) {
  const {
    item,
    exclude,
    include,
    operation = 'recalc',
    skipCopy
  } = action;
  // copy might have happened elsewhere (in recalc it almost always has)
  const nState = !skipCopy ? copy(state) : state;
  // to handle removal from a single path, include makes it so only certain refs are recalc'ed
  const refs = include || getRefs(item?.id, state.reverseLookup);
  refs.forEach(({
    path,
    queryName,
    value
  }) => {
    const {
      refType
    } = value;
    // sometimes recalc happens elsewhere before getting here
    if (exclude?.[path]?.[queryName]) {
      return;
    }
    if (refType === LIST) {
      const dataPath = Path([queryName]);
      const data = dataPath.get(state.data[path]);
      if (!data) return;
      if (operation === 'recalc') {
        const nItems = data?.items?.map(i => i?.id ? nState.lookup[i.id] : null);
        nState.data[path] = dataPath.copy(nState.data[path] || {}, {
          ...data,
          items: nItems,
          filteredItems: getFiltered(nItems, data?.filter)
        });
      }
      if (operation === 'remove') {
        const nItems = data?.items?.filter(i => i?.id !== item?.id);
        nState.data[path] = dataPath.copy(nState.data[path] || {}, {
          ...data,
          total: data?.total ? data.total - 1 : null,
          items: nItems,
          filteredItems: getFiltered(nItems, data?.filter)
        });
      }
    }
    if (refType === ITEM) {
      if (operation === 'remove') {
        delete nState.data[path];
      }
      if (operation === 'set') {
        const {
          originalAction: {
            path: aPath
          }
        } = action;
        if (path !== aPath) {
          delete nState.data[path];
        }
      }
    }
    if (refType === TABLE) {
      const dataPath = Path([queryName]);
      const data = dataPath.get(state.data[path]);
      if (!data) return;
      if (operation === 'recalc') {
        const nItems = data?.items?.map(cItem => {
          if (!cItem) {
            return cItem;
          }
          let same = true;
          const toReturn = {};
          Object.entries(cItem).forEach(([key, dev]) => {
            if (dev !== nState.lookup[dev.id]) {
              same = false;
            }
            toReturn[key] = nState.lookup[dev.id];
          });
          return same ? cItem : toReturn;
        });
        nState.data[path] = dataPath.copy(nState.data[path] || {}, {
          ...data,
          items: nItems,
          filteredItems: getFiltered(nItems, data?.filter)
        });
      }
      if (operation === 'remove') {
        const nItems = data?.items?.filter(cItem => Object.value(cItem).every(dev => dev?.id !== item.id));
        nState.data[path] = dataPath.copy(nState.data[path] || {}, {
          ...data,
          total: data?.total ? data.total - 1 : null,
          items: nItems,
          filteredItems: getFiltered(nItems, data?.filter)
        });
      }
    }
  });
  if (operation === 'remove' && include) {
    refs.forEach(ref => {
      const {
        path,
        queryName
      } = ref;
      delete nState.reverseLookup[item?.id][path][queryName];
    });
  } else if (operation === 'remove') {
    delete nState.reverseLookup[item?.id];
  }
  return nState;
}
function bulkRecalc(state, action) {
  const {
    items,
    exclude,
    include,
    operation,
    skipCopy
  } = action;
  if (!items) {
    return state;
  }
  let nState = !skipCopy ? copy(state) : state;
  for (let i = 0; i < items.length; i++) {
    nState = recalc(nState, {
      item: items[i],
      exclude,
      include,
      operation,
      skipCopy: true
    });
  }
  return nState;
}
function list(state, prefix, action) {
  if (isTableResult(action)) {
    return table(state, action); // no prefix in these
  }
  let nState = copy(state);
  const {
    data,
    path,
    queryName,
    body,
    options
  } = action;
  const qName = getQueryName(queryName);
  const current = nState.data?.[path]?.[qName] || {};
  const aggOptions = body?.aggs || options?.aggs;
  const entry = {
    total: data.total,
    perPage: data.perPage,
    search: data.search || null,
    sort: data.sort || null,
    items: null,
    aggOptions,
    aggs: data.aggs,
    filter: data.filter || current.filter,
    status: Constants.INTERFACE_STATUS_DONE,
    time: Date.now()
  };
  let id;
  const items = data.items;
  if (data.total == null || current.items == null || current.items.length !== data.total ||
  // allow null to equal undefined
  // eslint-disable-next-line eqeqeq
  current.search != data.search && !equals(current.search, data.search) ||
  // allow null to equal undefined
  // eslint-disable-next-line eqeqeq
  current.sort != data.sort && !equals(current.sort, data.sort)) {
    entry.items = new Array(data.total);
    current?.items?.forEach(item => {
      nState.reverseLookup[item.id] = Path([path, qName]).copy(nState.reverseLookup[item.id] || {}, {});
      delete nState.reverseLookup[item.id][path][qName];
    });
  } else {
    entry.items = [].concat(current.items || []);
  }

  // copy only new items into the array
  for (let n = 0, tn = data.startIndex; n < items.length; n++, tn++) {
    id = prefix ? prefix + items[n].id : items[n].id;
    entry.items[tn] = items[n];
    nState.lookup[id] = items[n];
    // reverseLookup has already been cloned once, don't do it again
    nState.reverseLookup[id] = Path([path, qName]).copy(nState.reverseLookup[id] || {}, {
      refType: LIST
    });
  }
  entry.filteredItems = getFiltered(entry.items, entry.filter);
  nState.data[path] = {
    ...nState.data[path],
    [qName]: entry
  };
  nState = bulkRecalc(nState, {
    items: data?.items,
    exclude: {
      [path]: {
        [qName]: true
      }
    },
    skipCopy: true
  });
  return nState;
}
function table(state, action) {
  const nState = copy(state);
  const {
    data,
    path,
    queryName
  } = action;
  const qName = getQueryName(queryName);
  const entry = {
    total: data.total,
    perPage: data.perPage,
    items: null,
    filter: data.filter,
    status: Constants.INTERFACE_STATUS_DONE,
    time: Date.now()
  };
  // Currently this code will remove other ids in the list. Might be able to be
  // clever and look at search, sort
  const cEntry = nState.data?.[path]?.[qName] || {};
  if (cEntry && cEntry.items && cEntry.items.length === data.total) {
    entry.items = [].concat(cEntry.items || []);
  } else {
    entry.items = new Array(data.total);
  }
  const refs = data.refs;
  if (refs) {
    Object.keys(refs).forEach(k => {
      nState.lookup[k] = refs[k];
      nState.reverseLookup[k] = Path([path, qName]).copy(nState.reverseLookup[k] || {}, {
        refType: TABLE
      });
    });
  }

  // copy refs into lookup
  const items = data.items;
  for (let n = 0, tn = data.startIndex; n < items.length; n++, tn++) {
    const row = {};
    Object.entries(items[n]).forEach(([key, id]) => {
      row[key] = nState.lookup[id];
    });
    entry.items[tn] = row;
  }

  // calc filtered items
  entry.filteredItems = getTableFiltered(entry.items, entry.filter);
  nState.data[path] = {
    ...nState.data[path],
    [qName]: entry
  };
  return nState;
}
function create(state, prefix, action) {
  // A create is adding an object to a list. The path represents the list
  // When the result comes back, it should be stored in its own path, not
  // the lists' path
  // copy
  let nState = copy(state);
  const {
    data,
    path,
    queryName,
    type
  } = action;
  const qName = getQueryName(queryName);
  const id = prefix ? prefix + data.id : data.id;
  nState.lookup[id] = data;
  if (type === Constants.INTERFACE_CREATE_ADD && nState.data[path]) {
    // add to all queries if no search
    Object.entries(nState.data[path]).forEach(([q, value]) => {
      if (value?.search) {
        return;
      }
      const nItems = [].concat(value?.items || [], [data]);
      nState.data[path] = {
        ...nState.data[path],
        [q]: {
          ...value,
          status: qName === q ? Constants.INTERFACE_STATUS_DONE : value?.status,
          items: nItems,
          total: (value.total || 0) + 1
        }
      };
      nState.data[path][q].filteredItems = getFiltered(nItems, value.filter);
      nState.reverseLookup[id] = Path([path, q]).copy(nState.reverseLookup[id] || {}, {
        refType: LIST
      });
    });
  } else if (action.type === Constants.INTERFACE_CREATE_SET) {
    nState.data[path] = {
      ...nState.data[path],
      [qName]: {
        ...nState.data[path]?.[qName],
        status: Constants.INTERFACE_STATUS_DONE,
        id: action.data && action.data.id,
        // only update time in the obj
        time: Date.now()
      }
    };
    nState.reverseLookup[id] = Path([path, qName]).copy(nState.reverseLookup[id] || {}, {
      refType: ITEM
    });
  }

  // Set the object's path
  nState.data[`${path}/${id}`] = {
    ...nState.data[`${path}/${id}`],
    [qName]: {
      status: Constants.INTERFACE_STATUS_DONE,
      id,
      time: Date.now()
    }
  };
  nState.reverseLookup[id] = Path([`${path}/${id}`, qName]).copy(nState.reverseLookup[id], {
    refType: ITEM
  });
  nState = recalc(nState, {
    item: data,
    skipCopy: true,
    operation: action.type === Constants.INTERFACE_CREATE_SET ? 'set' : 'recalc',
    originalAction: action
  });
  return nState;
}
function sendMessage(state, prefix, action) {
  // A create is adding an object to a list. The path represents the list
  // When the result comes back, it should be stored in its own path, not
  // the lists' path
  // copy
  const nState = copy(state);
  const {
    data,
    path,
    queryName
  } = action;
  const qName = getQueryName(queryName);
  const id = data.msgId;
  nState.lookup[data.msgId] = data;
  const current = {
    ...nState.data[path]?.[qName],
    status: Constants.INTERFACE_STATUS_DONE,
    items: [].concat(nState.data[path]?.[qName]?.items || [], [data])
  };
  current.filteredItems = getFiltered(current.items.map(item => nState.lookup[item]), current.filter);
  nState.data[path] = {
    ...nState.data[path],
    [qName]: current
  };

  // Set the object's path
  nState.data[`${path}/${id}`] = {
    ...nState.data[`${path}/${id}`],
    [qName]: {
      status: Constants.INTERFACE_STATUS_DONE,
      id
    }
  };
  return nState;
}
function remove(state, prefix, action) {
  const {
    data,
    parentKey,
    path,
    parentPath
  } = action;
  const id = data ? prefix ? prefix + data.id : data.id : undefined;
  let nState = copy(state);
  if (data?.type === 'relationshipRemoved') {
    // delete the reverse lookup of this path

    if (parentPath && id) {
      const refs = getRefs(id, nState.reverseLookup); // { path, queryName, value }
      nState = recalc(nState, {
        item: {
          id
        },
        operation: 'remove',
        skipCopy: true,
        include: refs.filter(ref => ref.path === parentPath)
      });
    }
    delete nState.data[path];
    nState.reverseLookup = Path([id, path]).copy(nState.reverseLookup, {});
    delete nState.reverseLookup[id][path];
    if (parentKey) {
      let item = nState.lookup[id];
      if (item?.data) {
        item = {
          ...item,
          data: {
            ...item.data
          }
        };
        delete item.data[parentKey];
        nState.lookup[id] = item;
        nState = recalc(nState, {
          item: {
            id
          },
          skipCopy: true
        });
      }
    }
  } else if (data?.type === 'deviceRemoved') {
    // remove from everywhere
    nState = recalc(nState, {
      item: {
        id
      },
      operation: 'remove',
      skipCopy: true
    });
    delete nState.lookup[id];
  }
  return nState;
}
function get(state, prefix, action, opts) {
  if (isTableResult(action)) {
    return table(state, action); // no prefix in these
  }
  const {
    data,
    path,
    queryName
  } = action;
  // prevents extra work when called internally
  let nState = !opts?.skipCopy ? copy(state) : state;
  const qName = getQueryName(queryName);
  const id = data ? prefix ? prefix + data.id : data.id : undefined;
  if (id && data) {
    nState.lookup[id] = data;
    nState.reverseLookup = Path([id, path, qName]).copy(nState.reverseLookup, {
      refType: ITEM
    });
  }
  nState.data[path] = {
    ...nState.data[path],
    [qName]: {
      status: Constants.INTERFACE_STATUS_DONE,
      id,
      time: Date.now()
    }
  };
  nState = recalc(nState, {
    item: data,
    exclude: {
      [path]: {
        [qName]: true
      }
    },
    skipCopy: true
  });
  return nState;
}

// Identical to GET!!!
function update(state, prefix, action) {
  if (isTableResult(action)) {
    return table(state, action); // no prefix in these
  }
  let nState = copy(state);
  const {
    data,
    path,
    queryName
  } = action;
  const qName = getQueryName(queryName);
  const id = data ? prefix ? prefix + data.id : data.id : undefined;
  if (id && data) {
    nState.lookup[id] = data;
    nState.reverseLookup = Path([id, path, qName]).copy(nState.reverseLookup, {
      refType: ITEM
    });
  }
  nState.data[path] = {
    ...nState.data[path],
    [qName]: {
      status: Constants.INTERFACE_STATUS_DONE,
      id,
      time: Date.now()
    }
  };
  nState = recalc(nState, {
    item: data,
    exclude: {
      [path]: {
        [qName]: true
      }
    },
    skipCopy: true
  });
  return nState;
}
function status(state, path, action) {
  const {
    queryName,
    status,
    err,
    clearOnError
  } = action;
  const qName = getQueryName(queryName);
  const nState = copy(state, true, false);
  const shouldClear = err && clearOnError;
  const current = shouldClear ? undefined : nState.data?.[path]?.[qName];
  nState.data[path] = {
    ...(shouldClear ? undefined : nState.data?.[path]),
    [qName]: {
      ...current,
      status,
      error: err
    }
  };
  return nState;
}
function combineItems(items, newData) {
  if (!newData) {
    return items;
  }
  const {
    startIndex,
    items: newDataItems
  } = newData;
  const total = Math.max(items?.length || 0, startIndex + (newDataItems?.length || 0) || 0);
  let newItems;
  if (items) {
    newItems = [...items];
    newItems.length = total;
  } else {
    newItems = new Array(total);
  }
  for (let i = startIndex; i < startIndex + (newDataItems?.length || 0); i++) {
    newItems[i] = newData.items[i - startIndex];
  }
  return newItems;
}
function isSameQuery(q1, q2) {
  if (q1 && !q2 || !q1 && q2) {
    return false;
  }
  return q1 === q2 ||
  // if we get here q1 and q2 exist

  q1.start === q2.start && q1.end === q2.end && equals(q1.order, q2.order) && equals(q1.where, q2.where) && equals(q1.paths, q2.paths);
}
function history(state, queryName, action) {
  const qName = getQueryName(queryName);
  const nState = copy(state);
  const {
    data,
    path,
    time
  } = action;
  let id = path.split('/');
  id = id[id.length - 1];
  id = `${id}/${qName}`;
  const oldData = nState.lookup[id];

  // ensure start, end, order, where, and paths are the same
  let newItems;
  if (!oldData || isSameQuery(oldData, data)) {
    // same query or new place in state
    newItems = combineItems(oldData?.items, data);
  } else if (!isSameQuery(oldData, data) && time > (oldData?.time || 0)) {
    // diff query, but also newer query than current
    newItems = combineItems(null, data);
  } else {
    // ignore this update D:
    return state;
  }
  if (id && data) {
    nState.lookup[id] = {
      ...nState.lookup[id],
      ...data,
      items: newItems,
      time: Math.max(oldData?.time || 0, time)
    };
  }
  nState.data[path] = {
    ...nState.data[path],
    [qName]: {
      status: Constants.INTERFACE_STATUS_DONE,
      id,
      time: Date.now()
    }
  };
  return nState;
}
function getFiltered(items, filter) {
  if (filter == null || filter.type == null) {
    return items;
  }
  const f = Interface.getFilter(filter.type);
  if (f) {
    return f(filter, items);
  }
  return items;
}
function getTableFiltered(items, filter) {
  return getFiltered(items, filter);
}
function tableFilter(state, action) {
  const nState = copy(state);
  const {
    filter,
    path,
    queryName
  } = action;
  const qName = getQueryName(queryName);
  const data = nState.data[path]?.[qName] || {};
  const entry = {
    ...data,
    status: Constants.INTERFACE_STATUS_DONE,
    filter,
    filteredItems: getTableFiltered(data.items, filter)
  };
  nState.data[path] = {
    ...nState.data[path],
    [qName]: entry
  };
  return nState;
}
function filter(state, action) {
  if (isTableResult(action)) {
    return tableFilter(state, action); // no prefix in these
  }

  // never filter a single item
  if (isSingleResult(state, action)) {
    return state;
  }
  const nState = copy(state);
  const {
    filter,
    path,
    queryName
  } = action;
  const qName = getQueryName(queryName);
  const data = nState.data[path]?.[qName] || {};
  const entry = {
    ...data,
    status: Constants.INTERFACE_STATUS_DONE,
    filter,
    filteredItems: Array.isArray(data.items) ? getFiltered(data.items.map(item => state.lookup[item]), filter) : null
  };
  nState.data[path] = {
    ...nState.data[path],
    [qName]: entry
  };
  return nState;
}
reducer.createSelectors = (key, createSetStateKey = false) => {
  return createSelectors(key, Access, createSetStateKey);
};

// Set up initial defaults to make methods available immediately with default state key
const selectors = reducer.createSelectors('models', true);
Object.keys(selectors).forEach(key => {
  reducer[key] = selectors[key];
});
export default reducer;